"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useEffect, useState } from "react";
import { Star, ShieldCheck, Sparkles, ChevronLeft, ChevronRight } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Swiper, SwiperSlide, useSwiper } from "swiper/react";
import { A11y, Navigation, Pagination } from "swiper/modules";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/a11y";
interface ReviewItem {
  id: number;
  author: string;
  location: string;
  role: string;
  rating: number;
  product: string;
  quote: string;
  date: string;
  avatarSrc: string;
  avatarSrcset: string;
  avatarSizes: string;
}
const reviewsData: ReviewItem[] = [{
  id: 1,
  author: "María González",
  location: "CDMX",
  role: "Compradora verificada",
  rating: 5,
  product: "Kit completo de ritual",
  quote: "Desde que incorporé el ritual completo de Spora, mi piel despertó con una luminosidad suave que no había logrado antes. La textura de la crema facial se absorbe al instante y el té por las mañanas me da una claridad serena.",
  date: "Hace 2 semanas",
  avatarSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796619/img/client_maria_portrait.png",
  avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796619/img/client_maria_portrait.png-wvc-srcset",
  avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796619/img/client_maria_portrait.png-wvc-sizes"
}, {
  id: 2,
  author: "Lucía Paredes",
  location: "Guadalajara",
  role: "Compradora verificada",
  rating: 5,
  product: "Crema facial Maitake & Tinctura",
  quote: "La crema Maitake tiene un aroma terroso y reconfortante delicioso. Siento la barrera cutánea protegida frente al clima seco, y la tintura me acompaña como una pausa de equilibrio antes de iniciar el trabajo.",
  date: "Hace 1 mes",
  avatarSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796619/img/client_lucia_portrait.png",
  avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796619/img/client_lucia_portrait.png-wvc-srcset",
  avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796619/img/client_lucia_portrait.png-wvc-sizes"
}, {
  id: 3,
  author: "Ana Morales",
  location: "Monterrey",
  role: "Compradora verificada",
  rating: 5,
  product: "Crema corporal Reishi",
  quote: "Saber de dónde provienen los extractos y la dedicación botánica detrás de cada frasco me da total tranquilidad. La crema corporal deja la piel aterciopelada y profundamente nutrida sin sensación pesada.",
  date: "Hace 3 semanas",
  avatarSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796619/img/client_ana_portrait.png",
  avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796619/img/client_ana_portrait.png-wvc-srcset",
  avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796619/img/client_ana_portrait.png-wvc-sizes"
}];
function SwiperNavigationButtons() {
  const swiper = useSwiper();
  return <div className="flex items-center gap-2">
      <Button variant="outline" size="icon" aria-label="Reseña anterior" onClick={() => swiper.slidePrev()} className="size-10 rounded-full border-border bg-card text-foreground transition-[color,background-color,border-color,box-shadow] hover:border-primary hover:text-primary">
        <ChevronLeft className="size-4" />
      </Button>
      <Button variant="outline" size="icon" aria-label="Siguiente reseña" onClick={() => swiper.slideNext()} className="size-10 rounded-full border-border bg-card text-foreground transition-[color,background-color,border-color,box-shadow] hover:border-primary hover:text-primary">
        <ChevronRight className="size-4" />
      </Button>
    </div>;
}
export default function Reviews() {
  const prefersReduced = useReducedMotion();
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  return <section id="reseñas-de-clientas" data-nav="light" className="relative bg-background py-16 text-foreground md:py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-14">
        {/* Header Section */}
        <motion.div initial={prefersReduced ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1]
      }} className="flex flex-col items-start justify-between gap-6 border-b border-border pb-10 md:flex-row md:items-end">
          <div className="max-w-2xl space-y-3">
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-accent/60 px-3.5 py-1 text-xs font-semibold uppercase tracking-wider text-accent-foreground">
              <Sparkles className="size-3.5 text-primary" />
              <span>Voces de la comunidad</span>
            </div>
            <h2 className="font-serif text-3xl font-medium tracking-tight text-foreground sm:text-4xl md:text-5xl">
              Experiencias de nuestro ritual
            </h2>
            <p className="text-base text-muted-foreground sm:text-lg">
              Opiniones reales de quienes cuidan su piel y bienestar con la sabiduría de los hongos funcionales.
            </p>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5 rounded-2xl border border-border bg-card px-4 py-2 text-sm shadow-xs">
              <div className="flex text-primary">
                {Array.from({
                length: 5
              }).map((_, i) => <Star key={i} className="size-4 fill-primary text-primary" />)}
              </div>
              <span className="font-semibold text-foreground">5.0</span>
              <span className="text-xs text-muted-foreground">(+480 reseñas)</span>
            </div>
          </div>
        </motion.div>

        {/* Carousel Area */}
        <div className="mt-10">
          <Swiper modules={[Navigation, Pagination, A11y]} spaceBetween={24} slidesPerView={1} breakpoints={{
          640: {
            slidesPerView: 1.5,
            spaceBetween: 24
          },
          768: {
            slidesPerView: 2,
            spaceBetween: 24
          },
          1024: {
            slidesPerView: 3,
            spaceBetween: 28
          }
        }} loop={true} pagination={{
          clickable: true,
          bulletClass: "inline-block size-2 rounded-full bg-muted-foreground/30 mx-1.5 cursor-pointer transition-[background-color,transform]",
          bulletActiveClass: "!bg-primary !scale-125"
        }} a11y={{
          prevSlideMessage: "Reseña anterior",
          nextSlideMessage: "Siguiente reseña"
        }} className="pb-14">
            {reviewsData.map((review, i) => <SwiperSlide key={review.id} data-index={i} className="h-auto">
                <Card className="flex h-full flex-col justify-between rounded-2xl border border-border bg-card p-6 shadow-xs transition-[transform,border-color,box-shadow] duration-300 hover:border-primary/40 hover:shadow-md md:p-8">
                  <CardContent className="flex h-full flex-col justify-between p-0">
                    <div className="space-y-4">
                      {/* Top Row: Stars & Badge */}
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex text-primary" aria-label={`Calificación: ${review.rating} de 5 estrellas`}>
                          {Array.from({
                        length: review.rating
                      }).map((_, starIndex) => <Star key={starIndex} className="size-4 fill-primary text-primary" />)}
                        </div>
                        <Badge variant="secondary" className="flex items-center gap-1 rounded-full border border-border/80 bg-accent/70 px-2.5 py-0.5 text-xs font-medium text-accent-foreground">
                          <ShieldCheck className="size-3 text-primary" />
                          <span>{review.role}</span>
                        </Badge>
                      </div>

                      {/* Product Tag */}
                      <div className="pt-1">
                        <span className="rounded-md bg-secondary/80 px-2.5 py-1 text-xs font-medium text-secondary-foreground">
                          {review.product}
                        </span>
                      </div>

                      {/* Quote */}
                      <blockquote className="pt-2 text-sm leading-relaxed text-foreground sm:text-base">
                        &ldquo;{review.quote}&rdquo;
                      </blockquote>
                    </div>

                    {/* Bottom Row: Customer Info */}
                    <div className="mt-8 flex items-center gap-3.5 border-t border-border/60 pt-5">
                      <SafeHydrate><Avatar className="size-11 border border-border shadow-2xs">
                        <AvatarImage src={review.avatarSrc} data-wvc-srcset={review.avatarSrcset} data-wvc-sizes={review.avatarSizes} alt={review.author} className="object-cover" loading="lazy" />
                        <AvatarFallback className="bg-secondary text-xs font-semibold text-secondary-foreground">
                          {review.author.split(" ").map(namePart => namePart[0]).slice(0, 2).join("")}
                        </AvatarFallback>
                      </Avatar></SafeHydrate>

                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-foreground">
                          {review.author}
                        </p>
                        <p className="truncate text-xs text-muted-foreground">
                          {review.location} • {review.date}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </SwiperSlide>)}

            {/* Custom Bottom Controls */}
            <div className="mt-6 flex items-center justify-between">
              <div className="flex items-center text-xs text-muted-foreground">
                <span>Desliza para explorar más testimonios</span>
              </div>
              <SwiperNavigationButtons />
            </div>
          </Swiper>
        </div>

        {/* Reassurance Footer Pill */}
        <div className="mt-6 flex flex-wrap items-center justify-center gap-6 rounded-2xl border border-border/60 bg-card/60 p-4 text-center text-xs text-muted-foreground sm:text-sm">
          <div className="flex items-center gap-2">
            <span className="size-2 rounded-full bg-primary" />
            <span>Formulado en México con extractos puros</span>
          </div>
          <div className="hidden h-3 w-px bg-border sm:block" />
          <div className="flex items-center gap-2">
            <span className="size-2 rounded-full bg-primary" />
            <span>Ingredientes botánicos y micológicos trazables</span>
          </div>
          <div className="hidden h-3 w-px bg-border sm:block" />
          <div className="flex items-center gap-2">
            <span className="size-2 rounded-full bg-primary" />
            <span>Empaque de vidrio ámbar sustentable</span>
          </div>
        </div>
      </div>
    </section>;
}